"use client";

import Editor from "@/components/common/editor";
import { MentionProp } from "@/types/post";
import ProfilePicture from "@/components/common/profile-picture";
import { COMMENTS_API } from "@/lib/constants";
import { formatImageUrl } from "@/lib/utils";
import { useAuth } from "@/providers/auth-provider";
import { useModal } from "@/providers/modal-provider";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2Icon, PlusSquare } from "lucide-react";
import { useRef, useState } from "react";
import toast from "react-hot-toast";
import { ModalType } from "@/providers/modal-provider.types";

const CommentInputField = ({ productId }: { productId: string }) => {
	const queryClient = useQueryClient();
	const { user, isLoggedIn } = useAuth();
	const { openModal } = useModal();

	const isFounder = user?.isFounder || false;
	const isVerified = user?.isVerified || false;
	const profileImageUrl = formatImageUrl(user?.image);

	// TODO (ruthvik): Fix types
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const commentInputRef = useRef<any | null>(null);
	const [comment, setComment] = useState<string>("");
	const [mentions, setMentions] = useState<MentionProp[]>([]);
	const [errorMessage, setErrorMessage] = useState<string>("");
	const [isLoading, setIsLoading] = useState<boolean>(false);

	const postComment = async (productId: string, commentText: string) => {
		try {
			if (!isLoggedIn) {
				openModal(ModalType.Auth);
				return;
			}

			setIsLoading(true);

			// validations
			if (!comment.trim()) {
				setErrorMessage("Please enter a comment");
				setIsLoading(false);
				return;
			}

			const minCommentLength = 5;
			const maxCommentLength = 2000;
			if (comment.length < minCommentLength || comment.length > maxCommentLength) {
				setErrorMessage(
					`Comment should be between ${minCommentLength} and ${maxCommentLength} characters`
				);
				setIsLoading(false);
				return;
			}

			const url = new URL(COMMENTS_API);
			const response = await fetch(url, {
				method: "POST",
				body: JSON.stringify({ productId, commentText, mentions }),
			});

			const data = await response.json();
			const message = data?.message;

			if (response.ok) {
				toast.success(message);
			} else {
				toast.error(message);
			}

			if (commentInputRef.current?.clearEditorContent) {
				commentInputRef.current.clearEditorContent();
			}
		} catch (error) {
			console.log("Error occured while posting a comment on the product");
		} finally {
			setComment("");
			setIsLoading(false);
		}
	};

	const commentsMutation = useMutation({
		mutationFn: ({ productId, commentText }: { productId: string; commentText: string }) =>
			postComment(productId, commentText),
		onSuccess: () => queryClient.invalidateQueries({ queryKey: ["commentsProductSpecific"] }),
	});

	const onSubmit = (e: React.FormEvent) => {
		e.preventDefault();

		commentsMutation.mutate({
			productId,
			commentText: comment,
		});
	};

	const handleInputChange = ({
		content,
		mentions,
	}: {
		content: string;
		mentions?: MentionProp[];
	}) => {
		setComment(content);

		if (mentions && mentions?.length > 0) {
			setMentions(mentions);
		}

		setErrorMessage("");
	};

	return (
		<>
			<div className="flex items-center justify-start gap-3 rounded-md border bg-transparent px-5 py-2 transition focus-within:border-blue-500">
				<ProfilePicture
					profilePictureUrl={profileImageUrl}
					className={{
						container: "h-10 w-10 rounded-full",
						image: "rounded-full text-gray-600",
						badgeImage: "-bottom-3 h-7 w-7",
					}}
					isVerified={isVerified}
					isFounder={isFounder}
				/>

				<Editor
					content={comment}
					disable={!isLoggedIn}
					placeholder="Add a Comment"
					editorRef={commentInputRef}
					handleOnUpdate={handleInputChange}
					classNames={{ editor: "focus-visible:outline-none" }}
					disableOptions={[
						"Paragraph",
						"Heading 1",
						"Heading 2",
						"Heading 3",
						"Divider",
						"Line Break",
					]}
				/>

				<button type="button" onClick={onSubmit} disabled={isLoading}>
					{isLoading ? (
						<Loader2Icon className="h-6 w-6 animate-spin stroke-brand-primary" />
					) : (
						<PlusSquare className="h-6 w-6 text-brand-primary" />
					)}
				</button>
			</div>

			<span className="pt-2 text-xs text-red-400">{errorMessage}</span>
		</>
	);
};

export default CommentInputField;
